import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import {
  decodeGlobalVariableArgParam,
  getTargetIds,
  type ArgParam,
} from '@/dashboard/features/widgetsAPI/domain/referenceParam';
import { useWidgetArgParam } from '@/dashboard/features/widgetsAPI/composables/reference/useWidgetArgParam';
import {
  ABORT_API_KEYS,
  CHART_TYPE_TO_REQUEST_TYPE_MAP,
} from '@/dashboard/features/widgets/common/constants/define';
import { postV7DataMetaTargetColumn } from '@/api-client/data/queries/data-v7-controller/data-v7-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import {
  DisplayStyleType,
  type CalendarTimeRange,
  type WidgetChartType,
  type WidgetTimePeriod,
} from '@/dashboard/shared/types/widgets.types';
import type { OneDayCompareChartOption } from '@/dashboard/features/widgets/oneDayCompare/settings/types/oneDayCompareSettings.types';
import type { TagRequest, TagRequestTagType } from '@/api-client/data/model';
import type { TargetKeyNameItem } from '@/api-client/data/model/target-key-name-item';
import type { MetaStatV7Request } from '@/api-client/data/model/meta-stat-v7-request';
import { MetaStatV7RequestPeriod } from '@/api-client/data/model/meta-stat-v7-request-period';
import type { ColumnItem } from '@/api-client/data/model/column-item';
import { useRequestPeriod } from '@/dashboard/features/widgetsAPI/composables/useRequestPeriod';
import type { RequestPeriod } from '@/dashboard/features/widgetsAPI/domain/apiRequest.types';
import { buildOneDayCompareTimeWindows } from '@/dashboard/features/widgets/oneDayCompare/widget/services/apiParams.adapter';
import { getTzTimeNow } from '@/common/utils/time.utils';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { useGlobalFilterStore } from '@/dashboard/stores/global-filter';
import { useStandardVariablesStore } from '@/dashboard/stores/global-variables/standard-variables';
import { useTreeVariablesStore } from '@/dashboard/stores/global-variables/tree-variables';
import {
  applyPresetSelectionToRequestTargets,
  transformArgParamsWithPresetSelection,
} from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.utils';
import { filterClusterBrokerTags } from '@/dashboard/features/widgets/cluster/widget/domain/clusterBrokerFilter';
import { ReferenceInfo } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/referenceSettingOption.setup';
import { findKeyCustomStat } from '@/dashboard/features/widgets/common/domain/reference';
import { isCustomStatParentCategory } from '@/common/utils/statInfo.utils';
import { useSetStatDetailInfos } from './stat-detail';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { queryClient } from '@/common/lib/queryClient';
import { isNil } from 'lodash-es';

type WidgetId = string;

export interface RequestTagWithWidgetInfo {
  widget: {
    uuid: string;
    name: string;
    chartType: WidgetChartType;
    displayStyle?: DisplayStyleType;
    isInViewport?: boolean;
    timePeriod?: WidgetTimePeriod;
    calendarTimeRange?: CalendarTimeRange[];
    compareDateOption?: OneDayCompareChartOption['compareDate'];
  };
  chartData: {
    id: string;
    index: number;
    category: string;
    dataId?: string;
    targets: TagRequest[];
    categories?: string[];
  };
  referenceInfo?: ReferenceInfo;
}

export interface AllDashboardTargetsColumns {
  widgetUUID: string;
  chartDataId: string;
  chartType: WidgetChartType;
  targets: TargetKeyNameItem[];
  tableColumns: ColumnItem[];
  globalFilterIds?: number[];
  treeVariableIds?: number[];
  isInViewport?: boolean;
  displayStyle?: DisplayStyleType;
}

export interface ResponseTargetsEntry {
  chartDataId: string;
  targets: TargetKeyNameItem[];
}

const EMPTY_TARGET_ID_SET: ReadonlySet<string> = Object.freeze(new Set<string>());

const META_STAT_PERIOD_SET: ReadonlySet<string> = new Set<string>(
  Object.values(MetaStatV7RequestPeriod),
);

const isMetaStatPeriod = (value: string): value is MetaStatV7RequestPeriod =>
  META_STAT_PERIOD_SET.has(value);

export const buildMetaStatTimeFields = (
  requestPeriod: RequestPeriod,
): Pick<MetaStatV7Request, 'fromTime' | 'toTime' | 'period'> => {
  const { fromTime, toTime, period } = requestPeriod;
  return {
    ...(fromTime ? { fromTime } : {}),
    ...(toTime ? { toTime } : {}),
    ...(period && isMetaStatPeriod(period) ? { period } : {}),
  };
};

interface FetchDashboardTargetsColumnsOptions {
  /**
   * true일 때 현재 preset의 선택값으로 요청 타겟/argParam을 resolve하지 않는다.
   *
   * 기본 동작(false): 모드와 무관하게 preset transform이 적용된다.
   *   - view/preview 모드 + 범례 open → preset으로 좁혀진 타겟으로 위젯 데이터 요청
   *   - edit 모드에서도 동일하게 적용되므로, 위젯 레퍼런스 컬럼 설정처럼
   *     preset과 무관하게 전체 후보 타겟이 필요한 호출부는 반드시 true로 opt-out 해야 한다.
   *
   * true로 지정해야 하는 호출부:
   *   - 범례 편집 창 (`onOpenEditWindow`) — 글로벌 id 원본 유지
   *   - 편집 모드 초기 로드 (`baseGridLayout.setup.ts:293`) — 위젯 설정 UI용 전체 타겟
   *   - 위젯 설정 저장 후 재조회 (`widgetSettingsWindow.setup.ts:464`) — 동일 이유
   *
   * @see src/dashboard/features/dashboardLegend/SPEC.md — Preset Target Transform 섹션
   */
  skipPresetTransform?: boolean;
  transformTargets?: (targets: TagRequest[]) => TagRequest[];
  transformArgParam?: (argParam: ArgParam[] | undefined) => ArgParam[] | undefined;
}

export const useDashboardTargetsStore = defineStore('dashboardTargets', () => {
  const { getStatInfo } = useStatInfoStore();

  const allRequestTags = shallowRef<RequestTagWithWidgetInfo[]>([]);
  const allDashboardTargetsColumns = shallowRef<AllDashboardTargetsColumns[]>();
  const argParamsByWidget = ref<Map<WidgetId, ArgParam[] | undefined>>(new Map());

  // 대시보드 A→B 전환(같은 route, params.id 변경)은 라우트 섹션 이탈이 아니어서
  // disposeDashboardStores 가 동작하지 않는다. 그 사이 이전 대시보드 위젯의 argParam 이
  // Map 에 계속 남아 세션 내내 누적되므로, params.id 변경 시 정리한다.
  // (widget-uuid / widget-units 스토어와 동일한 패턴)
  const route = useRoute();
  watch(
    () => route.params.id,
    () => {
      argParamsByWidget.value.clear();
    },
  );

  const setAllRequestTags = (tags: RequestTagWithWidgetInfo[]) => {
    allRequestTags.value = tags;
  };

  const setAllDashboardTargetsColumns = (targets: AllDashboardTargetsColumns[]) => {
    allDashboardTargetsColumns.value = targets;
  };

  const originalTargetAliasMap = computed(() => {
    const map = new Map<string, string>();
    allDashboardTargetsColumns.value?.forEach(({ targets }) => {
      targets.forEach((target) => {
        if (target.id && target.alias) {
          map.set(target.id, target.alias);
        }
      });
    });
    return map;
  });

  const addDashboardTargetsColumn = (entry: AllDashboardTargetsColumns) => {
    const existing = allDashboardTargetsColumns.value ?? [];
    const index = existing.findIndex(
      (e) => e.widgetUUID === entry.widgetUUID && e.chartDataId === entry.chartDataId,
    );
    if (index === -1) {
      allDashboardTargetsColumns.value = [...existing, entry];
      return;
    }
    const next = existing.slice();
    next[index] = entry;
    allDashboardTargetsColumns.value = next;
  };

  const removeWidgetTargetsColumns = (widgetUUID: WidgetId) => {
    const existing = allDashboardTargetsColumns.value;
    if (!existing?.length) return;
    const next = existing.filter((e) => e.widgetUUID !== widgetUUID);
    if (next.length === existing.length) return;
    allDashboardTargetsColumns.value = next;
  };

  const copyTargetsColumns = (sourceUUID: WidgetId, targetUUID: WidgetId) => {
    const existing = allDashboardTargetsColumns.value;
    if (!existing?.length) return;
    const sourceEntries = existing.filter((e) => e.widgetUUID === sourceUUID);
    if (!sourceEntries.length) return;
    const copies = sourceEntries.map((entry) => ({ ...entry, widgetUUID: targetUUID }));
    allDashboardTargetsColumns.value = [...existing, ...copies];
  };

  const baselineTargetIdSets = computed(() => {
    const map = new Map<string, Map<string, ReadonlySet<string>>>();
    allDashboardTargetsColumns.value?.forEach(({ widgetUUID, chartDataId, targets }) => {
      let byChartData = map.get(widgetUUID);
      if (!byChartData) {
        byChartData = new Map();
        map.set(widgetUUID, byChartData);
      }
      const ids = new Set<string>();
      targets.forEach(({ id }) => {
        if (id != null) ids.add(`${id}`);
      });
      byChartData.set(chartDataId, ids);
    });
    return map;
  });

  const getBaselineTargetIdSet = (
    widgetUUID: WidgetId,
    chartDataId: string,
  ): ReadonlySet<string> => {
    return baselineTargetIdSets.value.get(widgetUUID)?.get(chartDataId) ?? EMPTY_TARGET_ID_SET;
  };

  const applyTargetsColumnsFromResponseEntries = (
    widgetUUID: WidgetId,
    entries: ReadonlyArray<ResponseTargetsEntry>,
  ): void => {
    const byChartDataId = new Map<string, ResponseTargetsEntry>();
    entries.forEach((entry) => {
      if (entry.targets.length === 0) return;
      byChartDataId.set(entry.chartDataId, entry);
    });

    if (byChartDataId.size === 0) return;

    const existing = allDashboardTargetsColumns.value;
    if (!existing?.length) return;

    let next = existing;
    let updates = 0;
    byChartDataId.forEach((entry, chartDataId) => {
      const idx = next.findIndex(
        (e) => e.widgetUUID === widgetUUID && e.chartDataId === chartDataId,
      );
      if (idx === -1) return;

      if (updates === 0) next = existing.slice();
      next[idx] = { ...next[idx], targets: entry.targets };
      updates += 1;
    });

    if (updates > 0) allDashboardTargetsColumns.value = next;
  };

  const extractWidgetCustomTargets = (
    widgetUUID: WidgetId,
    argParam: ArgParam[] | undefined,
  ): TagRequest[] => {
    const foundWidget = argParamsByWidget.value.get(widgetUUID) ?? argParam;
    if (!foundWidget) return [];

    return foundWidget.flatMap(({ targetIds }) => {
      const decodedTags = decodeGlobalVariableArgParam(targetIds?.value);
      if (!decodedTags) return [];

      return decodedTags.map(({ tagKey, tagType, tagValue }) => ({
        tagKey,
        tagType: tagType as TagRequestTagType,
        tagValue: [tagValue],
      }));
    });
  };

  const { getArgParams } = useWidgetArgParam(computed(() => ({})));
  const { dashboardApiController } = useDashboardViewStore();
  const globalFilterStore = useGlobalFilterStore();
  const treeVariablesStore = useTreeVariablesStore();
  const { getRequestPeriod } = useRequestPeriod();

  const shouldUseTargetCategory = (chartType: WidgetChartType) => {
    return ['STATUS', 'STATUS_SUMMARY', 'SERVER', 'TOPOLOGY'].includes(chartType);
  };

  const { setStatDetailInfos } = useSetStatDetailInfos(
    computed(() =>
      allRequestTags.value
        .filter(({ chartData }) => !!chartData.dataId)
        .map(({ chartData }) => ({ dataId: chartData.dataId!, category: chartData.category })),
    ),
  );

  const buildMetaStatRequest = (
    chartType: WidgetChartType,
    chartData: RequestTagWithWidgetInfo['chartData'],
    requestTargets: TagRequest[],
    argParam: ArgParam[] | undefined,
  ): MetaStatV7Request => {
    const statInfoForCategory = chartData.dataId
      ? getStatInfo({ statId: chartData.dataId })
      : undefined;
    const categoryForApi =
      statInfoForCategory && isCustomStatParentCategory(statInfoForCategory.parentCategory)
        ? statInfoForCategory.parentCategory
        : chartData.category;

    const baseRequest: MetaStatV7Request = {
      tags: requestTargets,
      argParam,
      category: categoryForApi ? categoryForApi : undefined,
      targetCategory: categoryForApi ? categoryForApi : undefined,
      categories: chartData.categories?.length ? chartData.categories : undefined,
    };

    if (!CHART_TYPE_TO_REQUEST_TYPE_MAP.tableRequests.includes(chartType)) {
      return baseRequest;
    }

    return {
      ...baseRequest,
      tableTargetCategory:
        shouldUseTargetCategory(chartType) && !isNil(chartData.category)
          ? (chartData.category.toUpperCase() as MetaStatV7Request['tableTargetCategory'])
          : undefined,
      dataId: chartData.dataId,
      targetIds: argParam?.at(0) ? getTargetIds(argParam[0]) : undefined,
    };
  };

  const fetchSingleTargetColumn = async (
    requestTag: RequestTagWithWidgetInfo,
    options: FetchDashboardTargetsColumnsOptions = {},
  ): Promise<AllDashboardTargetsColumns | null> => {
    const { widget, chartData, referenceInfo } = requestTag;

    const argParam = getArgParams({
      chartType: widget.chartType,
      chartDataIndex: chartData.index,
      chartDataId: chartData.id,
      statId: chartData.dataId ?? '',
      category: chartData.category,
      referenceInfo,
      widgetId: widget.uuid,
    });

    const refinedTargets = chartData.targets.length
      ? chartData.targets
      : extractWidgetCustomTargets(widget.uuid, argParam);

    const applyDefaultPresetTransform = !options.skipPresetTransform;
    const defaultTransformedTargets = applyDefaultPresetTransform
      ? applyPresetSelectionToRequestTargets({
          targets: refinedTargets,
          presetList: globalFilterStore.presetList,
          currentPresetId: globalFilterStore.currentPreset.userFilterId,
          treeVariables: treeVariablesStore.treeVariables,
        })
      : refinedTargets;
    const defaultTransformedArgParam = applyDefaultPresetTransform
      ? transformArgParamsWithPresetSelection({
          argParam,
          presetList: globalFilterStore.presetList,
          currentPresetId: globalFilterStore.currentPreset.userFilterId,
          treeVariables: treeVariablesStore.treeVariables,
        })
      : argParam;

    // Cluster 위젯은 단일 cubrid_broker 타겟만으로는 클러스터를 그릴 수 없으므로
    // meta-target-column 요청 tags 에서 cubrid_broker 관련 태그를 제외한다.
    const filteredTargets =
      widget.chartType === 'CLUSTER'
        ? filterClusterBrokerTags(
            defaultTransformedTargets,
            useStandardVariablesStore().standardVariables,
            useTreeVariablesStore().treeVariables,
          )
        : defaultTransformedTargets;

    const requestTargets = options.transformTargets
      ? options.transformTargets(filteredTargets)
      : filteredTargets;
    const requestArgParam = options.transformArgParam
      ? options.transformArgParam(argParam)
      : defaultTransformedArgParam;

    const baseRequest = buildMetaStatRequest(
      widget.chartType,
      chartData,
      requestTargets,
      requestArgParam,
    );

    const requests: MetaStatV7Request[] =
      widget.chartType === 'ONE_DAY_COMPARE'
        ? buildOneDayCompareTimeWindows(
            widget.compareDateOption,
            getTzTimeNow().startOf('day').toDate(),
          ).map((window) => ({
            ...baseRequest,
            ...window,
          }))
        : [
            {
              ...baseRequest,
              ...buildMetaStatTimeFields(
                getRequestPeriod(widget.timePeriod, widget.chartType, widget.calendarTimeRange),
              ),
            },
          ];

    const globalFilterIds = Array.from(
      new Set(
        refinedTargets
          .filter(({ tagType }) => tagType === 'globalVariable')
          .flatMap(({ tagValue }) => tagValue ?? [])
          .map((tagValue) => Number(tagValue))
          .filter((tagValue) => !Number.isNaN(tagValue)),
      ),
    );

    const treeVariableIds = Array.from(
      new Set(
        refinedTargets
          .filter(({ tagType }) => tagType === 'treeGlobalVariable')
          .flatMap(({ tagValue }) => tagValue ?? [])
          .map((tagValue) => Number(tagValue))
          .filter((tagValue) => !Number.isNaN(tagValue)),
      ),
    );

    const {
      data: { data = [] },
    } = await queryClient.fetchQuery({
      queryKey: ['dashboardTargetColumn', requests],
      queryFn: () =>
        postV7DataMetaTargetColumn(
          requests,
          {
            params: {
              frameName: FRAME_NAMES.DASHBOARD.TARGETS_COLUMN_DATA_LIST,
            },
          },
          dashboardApiController.getAbortSignal(ABORT_API_KEYS.TARGET_COLUMN) ?? undefined,
        ),
    });

    if (!data?.length) return null;

    return {
      widgetUUID: widget.uuid,
      chartDataId: chartData.id,
      chartType: widget.chartType,
      displayStyle: widget.displayStyle,
      targets: data[0].targets || [],
      tableColumns: data[0].tables?.[0]?.[0]?.columns || [],
      globalFilterIds,
      treeVariableIds,
      isInViewport: widget.isInViewport,
    };
  };

  const fetchDashboardTargetsColumns = async (
    requestTags = allRequestTags.value,
    options: FetchDashboardTargetsColumnsOptions = {},
  ) => {
    // getArgParams에서 statDetailInfos의 기본 argument 값을 참조하므로
    // 타겟 컬럼 요청 전에 stat 상세 정보를 먼저 로드해야 함
    await setStatDetailInfos();

    const results = await Promise.allSettled(
      requestTags.map((requestTag) => fetchSingleTargetColumn(requestTag, options)),
    );

    return results.reduce<AllDashboardTargetsColumns[]>((acc, result) => {
      if (result.status === 'fulfilled' && result.value !== null) {
        acc.push(result.value);
      }
      return acc;
    }, []);
  };

  /**
   * 대시보드 타겟 초기화 통합 함수
   * setAllRequestTags → fetchDashboardTargetsColumns → setAllDashboardTargetsColumns 순서로 실행
   */
  const initializeDashboardTargets = async (
    requestTags?: RequestTagWithWidgetInfo[],
    options: FetchDashboardTargetsColumnsOptions = {},
  ) => {
    if (requestTags) {
      setAllRequestTags(requestTags);
    }
    const columns = await fetchDashboardTargetsColumns(requestTags, options);
    setAllDashboardTargetsColumns(columns);
    return columns;
  };

  const setArgParamsByWidget = ({
    widgetId,
    argParams,
  }: {
    widgetId: WidgetId;
    argParams: ArgParam[] | undefined;
  }) => {
    if (!argParams || argParams.length === 0) return;

    const existingArgParams = argParamsByWidget.value.get(widgetId) || [];
    const argParamsMap = new Map(
      existingArgParams.map((argParam) => {
        const keyField = findKeyCustomStat(argParam);
        return [keyField ? argParam[keyField]?.value : null, argParam];
      }),
    );

    argParams.forEach((newArgParam) => {
      const keyField = findKeyCustomStat(newArgParam);
      if (!keyField) return;

      const keyValue = newArgParam[keyField]?.value;
      if (argParamsMap.has(keyValue)) {
        return;
      }
      argParamsMap.set(keyValue, newArgParam);
    });

    argParamsByWidget.value.set(widgetId, Array.from(argParamsMap.values()));
  };

  const clearWidgetArgParams = (widgetId: WidgetId) => {
    argParamsByWidget.value.delete(widgetId);
  };

  return {
    allDashboardTargetsColumns,
    originalTargetAliasMap,
    baselineTargetIdSets,
    setAllRequestTags,
    setAllDashboardTargetsColumns,
    addDashboardTargetsColumn,
    removeWidgetTargetsColumns,
    copyTargetsColumns,
    applyTargetsColumnsFromResponseEntries,
    getBaselineTargetIdSet,
    fetchDashboardTargetsColumns,
    initializeDashboardTargets,
    setArgParamsByWidget,
    clearWidgetArgParams,
  };
});
